import { AffiliateState } from "../../interfaces/affiliate";

export const LAUNCH_POLICIES = {
  reward_policy: "cash_points_v1",
  waiver_policy: "full_fill_or_none",
  command_receipt_policy: "expiring_v1",
  lifecycle_policy: "launch_v1",
} as const;
export const MAX_ALLOWLIST_WALLETS = 10000;

export type AffiliateSettings = {
  owner_fee_policy?: "" | "tier_floor_v1";
  ended_budget_retention_policy?: "" | "archive_v1";
  enabled: boolean;
  weekly_cash: boolean;
  allowlist: string[] | null;
  internal_accounts: string[] | null;
  aevo_usd_price: string;
  enrollment_mode: string;
  allowlist_limit: number;
  reward_policy: string;
  waiver_policy: string;
  command_receipt_policy: string;
  lifecycle_policy: string;
};

const addressList = (value: unknown) =>
  value === null ||
  (Array.isArray(value) && value.every((entry) => typeof entry === "string"));

// Configure replaces Settings on the backend. Read every field; never derive a
// replacement from form defaults or spread unrelated fields from the admin view.
export function affiliateSettings(data?: AffiliateState): AffiliateSettings {
  if (
    !data ||
    (data.owner_fee_policy !== undefined &&
      !["", "tier_floor_v1"].includes(data.owner_fee_policy)) ||
    (data.ended_budget_retention_policy !== undefined &&
      !["", "archive_v1"].includes(data.ended_budget_retention_policy)) ||
    typeof data.enabled !== "boolean" ||
    typeof data.weekly_cash !== "boolean" ||
    !addressList(data.allowlist) ||
    !addressList(data.internal_accounts) ||
    typeof data.aevo_usd_price !== "string" ||
    !/^[0-9]+(\.[0-9]+)?$/.test(data.aevo_usd_price) ||
    typeof data.enrollment_mode !== "string" ||
    !["", "allowlist", "open"].includes(data.enrollment_mode) ||
    !Number.isInteger(data.allowlist_limit) ||
    (data.allowlist_limit ?? 0) < 1 ||
    (data.allowlist_limit ?? 0) > MAX_ALLOWLIST_WALLETS ||
    Object.entries(LAUNCH_POLICIES).some(([key, policy]) => {
      const value = data[key as keyof typeof LAUNCH_POLICIES];
      return value !== "" && value !== policy;
    })
  )
    throw new Error(
      "Refresh the complete backend configuration before saving. Missing or unrecognized settings require a newer admin application."
    );
  return {
    ...(data.owner_fee_policy !== undefined
      ? { owner_fee_policy: data.owner_fee_policy }
      : {}),
    ...(data.ended_budget_retention_policy !== undefined
      ? { ended_budget_retention_policy: data.ended_budget_retention_policy }
      : {}),
    enabled: data.enabled,
    weekly_cash: data.weekly_cash,
    allowlist: data.allowlist,
    internal_accounts: data.internal_accounts!,
    aevo_usd_price: data.aevo_usd_price,
    enrollment_mode: data.enrollment_mode,
    allowlist_limit: data.allowlist_limit!,
    reward_policy: data.reward_policy!,
    waiver_policy: data.waiver_policy!,
    command_receipt_policy: data.command_receipt_policy!,
    lifecycle_policy: data.lifecycle_policy!,
  };
}

export const launchPrepared = (data?: AffiliateState) =>
  data?.program_version === 2 &&
  data.compact_state === true &&
  Object.entries(LAUNCH_POLICIES).every(
    ([key, policy]) => data[key as keyof typeof LAUNCH_POLICIES] === policy
  );
